'use strict';

const libFableServiceProviderBase = require('fable-serviceproviderbase');

/**
 * TypeComprehensionReadProvider
 *
 * The read-side counterpart to TypeComprehensionSaveProvider: hydrates the parts of a
 * ProjectType / ContractType that do NOT live on the base Meadow record —
 *   - statuses + change-order flags (ListDefault, via GET {Entity}/:id/Statuses),
 *   - the default-date definitions (*TypeDefaultDate rows),
 *   - a contract type's allowed project types (GET ContractType/:id/AllowedProjectTypes),
 *   - the prototype Project/Contract the type points at (Settings.PrototypeID{Project,Contract}).
 *
 * Returns PLAIN DATA (it does not mutate `_DisplaySettings`), so a bespoke editor loads it
 * into its own state. This is the single, shared implementation of those four reads —
 * mirroring the endpoints the legacy PSRS `_load*IntoDisplay` helpers use in
 * PlatformConfiguration-Application-Web.js — so the raw and bespoke paths cannot drift.
 * Registered as a fable service (resolve via `this.fable.TypeComprehensionReadProvider`).
 *
 * Exercised only at runtime in the browser against a live API — validate end-to-end on QA.
 */
class TypeComprehensionReadProvider extends libFableServiceProviderBase
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this.pict = this.fable;
		this.serviceType = 'TypeComprehensionReadProvider';
	}

	/** @returns {*} The shared HeadlightRestClient, or null when it isn't registered. */
	get _restClient()
	{
		return (this.fable && this.fable.HeadlightRestClient)
			|| (this.pict && this.pict.fable && this.pict.fable.HeadlightRestClient)
			|| (this.pict && this.pict.HeadlightRestClient)
			|| null;
	}

	/** @param {string} pEntity - 'ProjectType' | 'ContractType' */
	_idField(pEntity) { return (pEntity === 'ContractType') ? 'IDContractType' : 'IDProjectType'; }

	/**
	 * GET {Entity}/:id/Statuses → { Available:[names], RequiresChangeOrder:[names] }.
	 * Resolves to empty lists on any error / missing id (never rejects).
	 *
	 * @param {string} pEntity
	 * @param {number|string} pID
	 * @returns {Promise<{Available: Array<string>, RequiresChangeOrder: Array<string>}>}
	 */
	loadStatuses(pEntity, pID)
	{
		const tmpClient = this._restClient;
		return new Promise((resolve) =>
		{
			if (!tmpClient || !pID) { return resolve({ Available: [], RequiresChangeOrder: [] }); }
			tmpClient.getJSON(`${pEntity}/${pID}/Statuses`, (pError, pBody) =>
			{
				if (pError || !pBody) { return resolve({ Available: [], RequiresChangeOrder: [] }); }
				return resolve(
					{
						Available: Array.isArray(pBody.Available) ? pBody.Available.map((pV) => String(pV)) : [],
						RequiresChangeOrder: Array.isArray(pBody.RequiresChangeOrder) ? pBody.RequiresChangeOrder.map((pV) => String(pV)) : [],
					});
			});
		});
	}

	/**
	 * The type's default-date definitions → [{ Hash, Name, IDRow }] (server order).
	 *
	 * @param {string} pEntity
	 * @param {number|string} pID
	 * @returns {Promise<Array<{Hash:string, Name:string, IDRow:number}>>}
	 */
	loadDefaultDates(pEntity, pID)
	{
		const tmpClient = this._restClient;
		const tmpIDField = this._idField(pEntity);
		const tmpDateEntity = (pEntity === 'ContractType') ? 'ContractTypeDefaultDate' : 'ProjectTypeDefaultDate';
		const tmpRowIDField = (pEntity === 'ContractType') ? 'IDContractTypeDefaultDate' : 'IDProjectTypeDefaultDate';
		return new Promise((resolve) =>
		{
			if (!tmpClient || !pID) { return resolve([]); }
			tmpClient.getEntitySet(tmpDateEntity, `FBV~${tmpIDField}~EQ~${pID}`, (pError, pRecords) =>
			{
				if (pError || !Array.isArray(pRecords)) { return resolve([]); }
				return resolve(pRecords.map((pRow) => (
					{
						Hash: (pRow && typeof pRow.Hash === 'string') ? pRow.Hash : '',
						Name: (pRow && typeof pRow.Name === 'string') ? pRow.Name : '',
						IDRow: (pRow && Number(pRow[tmpRowIDField])) || 0,
					})));
			}, 0, 200000);
		});
	}

	/**
	 * GET ContractType/:id/AllowedProjectTypes → [ '12', '15', … ] (numeric IDProjectType strings).
	 *
	 * @param {number|string} pID
	 * @returns {Promise<Array<string>>}
	 */
	loadAllowedProjectTypes(pID)
	{
		const tmpClient = this._restClient;
		return new Promise((resolve) =>
		{
			if (!tmpClient || !pID) { return resolve([]); }
			tmpClient.getJSON(`ContractType/${pID}/AllowedProjectTypes`, (pError, pBody) =>
			{
				if (pError || !Array.isArray(pBody)) { return resolve([]); }
				return resolve(pBody.map((pV) => String(pV)));
			});
		});
	}

	/** Parse a type record's Settings defensively (object `Settings`, else `SettingsJSON` string). */
	parseSettings(pRecord)
	{
		if (pRecord && pRecord.Settings && typeof pRecord.Settings === 'object') { return pRecord.Settings; }
		if (pRecord && typeof pRecord.SettingsJSON === 'string' && pRecord.SettingsJSON.length > 0)
		{
			try { const tmpParsed = JSON.parse(pRecord.SettingsJSON); return (tmpParsed && typeof tmpParsed === 'object') ? tmpParsed : {}; }
			catch (pError) { return {}; }
		}
		return {};
	}

	/** The numeric prototype id a type points at (Settings.PrototypeID{Project,Contract}), or 0. */
	prototypeID(pEntity, pRecord)
	{
		const tmpSettings = this.parseSettings(pRecord);
		return Number((pEntity === 'ContractType') ? tmpSettings.PrototypeIDContract : tmpSettings.PrototypeIDProject) || 0;
	}

	/**
	 * Resolve the prototype Project/Contract a type points at → the prototype record, or null.
	 *
	 * @param {string} pEntity
	 * @param {Object} pRecord - the type record (its Settings carry the pointer).
	 * @returns {Promise<Object|null>}
	 */
	loadPrototype(pEntity, pRecord)
	{
		const tmpClient = this._restClient;
		const tmpIsContract = (pEntity === 'ContractType');
		const tmpProtoEntity = tmpIsContract ? 'Contract' : 'Project';
		const tmpProtoIDField = tmpIsContract ? 'IDContract' : 'IDProject';
		const tmpProtoID = this.prototypeID(pEntity, pRecord);
		return new Promise((resolve) =>
		{
			if (!tmpClient || !tmpProtoID) { return resolve(null); }
			tmpClient.getEntitySet(tmpProtoEntity, `FBV~${tmpProtoIDField}~EQ~${tmpProtoID}`, (pError, pRecords) =>
			{
				if (pError || !Array.isArray(pRecords) || !pRecords[0]) { return resolve(null); }
				return resolve(pRecords[0]);
			}, 0, 1);
		});
	}
}

module.exports = TypeComprehensionReadProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'TypeComprehensionReadProvider',
};
